import { Switch, Route, useLocation } from "wouter";
import { useState } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { WalletProvider } from "./contexts/wallet-context";
import { DappSignProvider } from "./contexts/dapp-sign-context";
import { ErrorBoundary } from "@/components/error-boundary";
import { SettingsModal } from "@/components/settings-modal";
import { useWallet } from "@/contexts/wallet-context";
import { useDisplay } from "@/hooks/use-display";
import Landing from "@/pages/landing";
import ConnectWallet from "@/pages/connect-wallet";
import Lobby from "@/pages/lobby";
import Table from "@/pages/table";
import UserAccount from "@/pages/user-account";
import LoyaltyRewards from "@/pages/loyalty-rewards";
import Slots from "@/pages/slots";
import BlackjackPage from "@/pages/blackjack";
import GbuxGamesPage from "@/pages/gbuxgames";
import AdminPage from "@/pages/admin";
import CallingProsPage from "@/pages/calling-pros";
import PrivacyPage from "@/pages/privacy";
import TermsPage from "@/pages/terms";
import NotFound from "@/pages/not-found";
import { ConnectionStatusBar } from "@/components/connection-status";
import { HardRefreshBanner } from "@/components/hard-refresh-banner";
import { PhantomMobileEntry } from "@/components/phantom-mobile-entry";
import { PuterEmbedBanner } from "@/components/puter-embed-banner";
import { MobileBottomNav } from "@/components/mobile-bottom-nav";
import {
  WalletAccountDrawer,
  WalletPlayChip,
} from "@/components/wallet-account-drawer";
import { AudioDock } from "@/components/audio-dock";

function Router() {
  return (
    <Switch>
      <Route path="/" component={Landing} />
      <Route path="/connect" component={ConnectWallet} />
      <Route path="/lobby" component={Lobby} />
      <Route path="/table/:tableId" component={Table} />
      <Route path="/account" component={UserAccount} />
      <Route path="/rewards" component={LoyaltyRewards} />
      <Route path="/gbuxgames" component={GbuxGamesPage} />
      <Route path="/gbux-games" component={GbuxGamesPage} />
      <Route path="/slots" component={Slots} />
      <Route path="/blackjack" component={BlackjackPage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/terms" component={TermsPage} />
      {/* Alias for ToS URL used by wallets / store listings */}
      <Route path="/tos" component={TermsPage} />
      <Route path="/admin" component={AdminPage} />
      <Route path="/callingpros" component={CallingProsPage} />
      <Route path="/calling-pros" component={CallingProsPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

/**
 * AppShell — lives inside WalletProvider so it can read the connected wallet.
 * Renders the router + a global SettingsModal wired to the active wallet address.
 * Exposes openSettings via a small floating gear that appears only when a wallet
 * is connected (useful during development / as a fallback entry point).
 */
function AppShell() {
  const { publicKey, connected } = useWallet();
  const [path] = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const walletAddress = publicKey?.toString() ?? undefined;
  // Display detection once — --ui-scale, portrait vertical table flags on <html>
  useDisplay();
  // Table page owns wallets on the right rail — chip would duplicate
  const onTable = /^\/table\//.test(path);

  return (
    <>
      <ConnectionStatusBar />
      <HardRefreshBanner />
      <PhantomMobileEntry />
      <PuterEmbedBanner />
      {!onTable && <WalletPlayChip />}
      <div className="app-shell-pad-mobile">
        <Router />
      </div>
      <MobileBottomNav />
      {/* Full drawer still for lobby/account; table uses right rail */}
      <WalletAccountDrawer />
      <AudioDock />

      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        walletAddress={walletAddress}
      />

      {connected && walletAddress && (
        <button
          onClick={() => setSettingsOpen(true)}
          aria-label="Open settings"
          className="settings-fab"
          style={{
            position: "fixed",
            bottom: "calc(72px + env(safe-area-inset-bottom))",
            right: 16,
            zIndex: 39,
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.7)",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            backdropFilter: "blur(6px)",
            boxShadow: "0 2px 12px rgba(0,0,0,0.4)",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <WalletProvider>
        <DappSignProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Toaster />
              <AppShell />
            </TooltipProvider>
          </QueryClientProvider>
        </DappSignProvider>
      </WalletProvider>
    </ErrorBoundary>
  );
}

export default App;
